import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiPlugin, StrapiApiScrollingLogo } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import ScrollingLogosWidget from '../../widgets/animated/ScrollingLogosWidget';
import AllIntegrationsShowcase from '../../widgets/showcases/AllIntegrationsShowcase';

type AIAssistantOperationsStaticProps = {
  scrollingLogos: StrapiApiScrollingLogo[];
  plugins: StrapiApiPlugin[];
};

export const getStaticProps: GetStaticProps<AIAssistantOperationsStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const plugins = await StrapiApiClient.getAllPlugins();
  const props: AIAssistantOperationsStaticProps = { scrollingLogos, plugins };
  return withGlobalDefaultStaticProps(context, { props });
};

const AIAssistantOperations: NextPageWithLayout<AIAssistantOperationsStaticProps> = (props) => {
  const { plugins, scrollingLogos } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.WorkAssistantForOperations);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Work Assistant for Internal Operations"
        title="Streamline ops with AI-powered visibility"
        description="Uncover inefficiencies, onboard faster, and boost collaboration—without the manual overhead."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="AI capabilities for Internal Operations" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-it-page-clear-performance-metrics'),
              orientation: 'left',
              title: 'Operational Clarity Starts with Data',
              description:
                'Analyze tickets, documents, and workflows to uncover inefficiencies and optimize processes at scale. Make smarter decisions with actionable insights for your entire operations team.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-private-secure-support'),
              orientation: 'right',
              title: 'Get New Hires Customer-Ready, Fast',
              description:
                'Streamline onboarding with AI-powered knowledge delivery—so every team member starts informed and confident. Reduce ramp-up time and ensure consistency across all departments.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-available-for-all-customers'),
              orientation: 'left',
              title: 'Work Better, Together',
              description:
                'Empower cross-functional teams with AI-driven knowledge sharing that speeds up decisions and strengthens collaboration. Break down silos and enable seamless cooperation across your organization.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-self-service'),
              orientation: 'right',
              title: 'Instant Answers for Internal Policies',
              description:
                'Give every employee immediate access to company policies, procedures, and forms. Reduce repetitive questions and keep everyone aligned.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-it-service-management'),
              orientation: 'left',
              title: 'Automated Compliance Monitoring',
              description:
                'Leverage AI to track compliance tasks, deadlines, and documentation. Minimize risk and ensure your operations always meet regulatory standards.',
            },
            {
              imageUrl: CDNAssets.getUrl('use-cases-page-private-secure-support'),
              orientation: 'right',
              title: 'Secure Internal Support',
              description:
                'Protect sensitive operational data while enabling fast, confidential support for all internal teams. Maintain privacy and trust across your organization.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <AllIntegrationsShowcase plugins={plugins} hideSubtitle={true} />
      </PageSection>
    </>
  );
};
AIAssistantOperations.displayName = 'AIAssistantOperations';
export default AIAssistantOperations;
